/**
 * Budgets API Client
 *
 * API client for fetching and updating budget data from the Monumenta backend.
 * Uses AWS Amplify API module for authenticated requests.
 *
 * @module api/budgets
 * @description Provides methods for interacting with budget-related endpoints.
 * All methods return typed responses matching the backend API contract.
 */

import { get, put } from '@/api/lib/amplify-wrapper'
import { createImpersonatedApiHeaders } from '@/lib/services/api-utils'
import type {
  ClientBudgetSettings,
  TrainingBudget,
  ProjectionsSummary,
  ClientBudgetsList,
  UpdateClientBudgetRequest,
  UpdateTrainingBudgetRequest,
} from '@/types/budgets'

const API_NAME = 'monumenta'

/**
 * Fetch budget settings for a specific client and year
 *
 * @param clientCode - Unique client identifier (e.g., '000054')
 * @param year - Budget year (e.g., 2025)
 * @returns Client budget settings or null if not found
 *
 * @example
 * const budget = await getClientBudget('000054', 2025)
 * console.log(budget?.settings.targetRevenue) // 16000000
 */
export const getClientBudget = async (
  clientCode: string,
  year: number
): Promise<ClientBudgetSettings | null> => {
  const queryParams = new URLSearchParams()
  queryParams.append('year', year.toString())

  try {
    const restOperation = get({
      apiName: API_NAME,
      path: `/monumenta/budgets/client/${clientCode}?${queryParams.toString()}`,
      options: {
        headers: createImpersonatedApiHeaders(),
      },
    })

    const response = await restOperation.response
    const data = await response.body.json()

    return data as unknown as ClientBudgetSettings
  } catch (error: unknown) {
    // Return null for 404 (budget not found), re-throw other errors
    if (error && typeof error === 'object' && 'response' in error) {
      const httpError = error as { response?: { statusCode?: number } }
      if (httpError.response?.statusCode === 404) {
        return null
      }
    }
    throw error
  }
}

/**
 * Update budget settings for a specific client and year
 *
 * @param clientCode - Unique client identifier
 * @param year - Budget year
 * @param updates - Partial budget settings to update
 * @returns Updated client budget settings
 *
 * @example
 * const updated = await updateClientBudget('000054', 2025, {
 *   settings: { targetRevenue: 18000000 }
 * })
 */
export const updateClientBudget = async (
  clientCode: string,
  year: number,
  updates: UpdateClientBudgetRequest
): Promise<ClientBudgetSettings> => {
  const restOperation = put({
    apiName: API_NAME,
    path: `/monumenta/budgets/client/${clientCode}/${year}`,
    options: {
      body: JSON.parse(JSON.stringify(updates)),
      headers: createImpersonatedApiHeaders(),
    },
  })

  const response = await restOperation.response
  const data = await response.body.json()

  return data as unknown as ClientBudgetSettings
}

/**
 * Fetch budget settings for all clients in a specific year
 *
 * @param year - Budget year
 * @returns List of client budgets for the year
 *
 * @example
 * const budgets = await listClientBudgets(2025)
 * console.log(budgets.totalClients) // 15
 */
export const listClientBudgets = async (year: number): Promise<ClientBudgetsList> => {
  const queryParams = new URLSearchParams()
  queryParams.append('year', year.toString())

  const restOperation = get({
    apiName: API_NAME,
    path: `/monumenta/budgets/clients?${queryParams.toString()}`,
    options: {
      headers: createImpersonatedApiHeaders(),
    },
  })

  const response = await restOperation.response
  const data = await response.body.json()

  return data as unknown as ClientBudgetsList
}

/**
 * Fetch company-wide training budget for a specific year
 *
 * @param year - Budget year
 * @returns Training budget with variance and utilization metrics
 *
 * @example
 * const training = await getTrainingBudget(2025)
 * console.log(training.utilizationPercent) // 57.5
 */
export const getTrainingBudget = async (year: number): Promise<TrainingBudget | null> => {
  const queryParams = new URLSearchParams()
  queryParams.append('year', year.toString())

  try {
    const restOperation = get({
      apiName: API_NAME,
      path: `/monumenta/budgets/training?${queryParams.toString()}`,
      options: {
        headers: createImpersonatedApiHeaders(),
      },
    })

    const response = await restOperation.response
    const data = await response.body.json()

    return data as unknown as TrainingBudget
  } catch (error: unknown) {
    // Return null for 404 (training budget not found), re-throw other errors
    if (error && typeof error === 'object' && 'response' in error) {
      const httpError = error as { response?: { statusCode?: number } }
      if (httpError.response?.statusCode === 404) {
        return null
      }
    }
    throw error
  }
}

/**
 * Update company-wide training budget for a specific year
 *
 * @param year - Budget year
 * @param updates - Budget and/or actual cost updates
 * @returns Updated training budget with recalculated variance
 *
 * @example
 * const updated = await updateTrainingBudget(2025, {
 *   budget: 550000,
 *   actualCost: 312000
 * })
 * console.log(updated.variance) // -238000
 */
export const updateTrainingBudget = async (
  year: number,
  updates: UpdateTrainingBudgetRequest
): Promise<TrainingBudget> => {
  const restOperation = put({
    apiName: API_NAME,
    path: `/monumenta/budgets/training/${year}`,
    options: {
      body: JSON.parse(JSON.stringify(updates)),
      headers: createImpersonatedApiHeaders(),
    },
  })

  const response = await restOperation.response
  const data = await response.body.json()

  return data as unknown as TrainingBudget
}

/**
 * Fetch company-wide projections summary for a specific year
 *
 * @param year - Budget year
 * @returns Aggregated projections across all clients
 *
 * @example
 * const projections = await getProjectionsSummary(2025)
 * console.log(projections.projections.totalRevenue) // 45000000
 * console.log(projections.clientCount) // 15
 */
export const getProjectionsSummary = async (year: number): Promise<ProjectionsSummary> => {
  const queryParams = new URLSearchParams()
  queryParams.append('year', year.toString())

  const restOperation = get({
    apiName: API_NAME,
    path: `/monumenta/budgets/projections?${queryParams.toString()}`,
    options: {
      headers: createImpersonatedApiHeaders(),
    },
  })

  const response = await restOperation.response
  const data = await response.body.json()

  return data as unknown as ProjectionsSummary
}
